import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  type Location
} from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from './context/AuthContext';
import { useProfile, useLeagues } from './hooks/useQueries';
import { useActiveLeague } from './hooks/useActiveLeague';
import { useNativeChrome } from './hooks/useNativeChrome';
import { usePushNotifications } from './hooks/usePushNotifications';
import { useOtaUpdater } from './hooks/useOtaUpdater';
import Welcome from './screens/Welcome';
import LandingPage from './screens/landing/LandingPage';
import Onboarding from './screens/Onboarding';
import CreateAccount from './screens/CreateAccount';
import CreateProfile from './screens/CreateProfile';
import Login from './screens/Login';
import ResetPassword from './screens/ResetPassword';
import ChooseSport from './screens/ChooseSport';
import ChooseLeague from './screens/ChooseLeague';
import RoundPlay from './screens/RoundPlay';
import PicksRoute from './screens/PicksRoute';
import RoundWaiting from './screens/RoundWaiting';
import RoundResults from './screens/RoundResults';
import Standings from './screens/Standings';
import Friends from './screens/Friends';
import LeaguesHub from './screens/leagues/Hub';
import CreateMiniLeague from './screens/leagues/Create';
import JoinMiniLeague from './screens/leagues/Join';
import ShareMiniLeague from './screens/leagues/Share';
import MiniLeagueStandings from './screens/leagues/Standings';
import ManageMiniLeague from './screens/leagues/Manage';
import Profile from './screens/Profile';
import UserProfile from './screens/UserProfile';
import ChallengeSetup from './screens/ChallengeSetup';
import Settings from './screens/Settings';
import EditProfile from './screens/EditProfile';
import GoPro from './screens/GoPro';
import Notifications from './screens/Notifications';
import HeadToHead from './screens/HeadToHead';
import MatchDetail from './screens/MatchDetail';
import IconShowcase from './screens/IconShowcase';
import SportIconShowcase from './screens/SportIconShowcase';
import AdminApp from './screens/admin/AdminApp';
import { SplashScreen } from './components/ui/SplashScreen';
import { SideNav } from './components/ui/SideNav';
import { TabBar, type TabKey } from './components/ui/TabBar';
import { NavStack } from './components/ui/NavStack';
import { LeagueSwitchProvider } from './components/ui/league-switch/LeagueSwitchTransition';
import { MiniLeaguePromo } from './components/ui/MiniLeaguePromo';
import { leagueIdFromPath, tabForPath, tabRoute, hideTabBarForPath, isDarkRoute } from './components/ui/appNav';
import { useLastLeague, readLastLeague } from './hooks/useLastLeague';
import { cn } from './lib/cn';
import { DURATION } from './lib/motion';

/** Pre-auth flow: welcome → onboarding → create account/profile, plus log in & reset. */
function AuthFlow() {
  const isNative = Capacitor.isNativePlatform();
  const home = isNative ? <Welcome /> : <LandingPage />;

  return (
    <NavStack className="h-full">
      {(location) => (
        <Routes location={location}>
          <Route path="/" element={home} />
          <Route path="/welcome" element={home} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/signup" element={<CreateAccount />} />
          <Route path="/login" element={<Login />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </NavStack>
  );
}

/**
 * The app's responsive frame, mounted once and kept persistent across navigation.
 *   Mobile  — full-bleed surface; the page content scrolls inside the transition
 *             viewport while the floating tab bar stays bolted to the bottom.
 *   Desktop — a left nav rail + a wide content area.
 * Centralising navigation here (active tab derived from the route, edge-swipe-back
 * on the viewport, tab bar / side nav as persistent chrome) is what lets only the
 * page animate between screens while everything around it holds still.
 */
function AppChrome({ children }: { children: (location: Location) => ReactNode }) {
  useNativeChrome();
  usePushNotifications();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Warm the (cached, public) leagues list once, app-wide. A league's currentRoundId
  // lives here, so the round/entry reads on the play hub can't start until it lands —
  // prefetching it in the persistent chrome means even a deep-link straight into a
  // league isn't waiting on a cold fetch before its standing/picks can load.
  useLeagues();
  const { leagueId: resolvedLeagueId } = useActiveLeague();
  // Prefer the league in the URL; fall back to the last league the player visited
  // (remembered in sessionStorage), then to their resolved active league. This
  // ensures the tab bar always returns to the correct league after a detour to a
  // non-league route like /u/:userId, /challenge/, /settings, etc.
  const leagueFromPath = leagueIdFromPath(pathname);
  const leagueId = leagueFromPath ?? readLastLeague() ?? resolvedLeagueId;
  // Keep the session memory up to date whenever a league is in the URL.
  useLastLeague(leagueFromPath);
  const activeTab = tabForPath(pathname);
  const onSelectTab = (key: TabKey) => navigate(tabRoute(key, leagueId), { replace: true });
  // When popping back from a chromeless route (fixture detail, picks, live board)
  // the pathname flips before the slide finishes — hold the tab bar hidden for one
  // page transition so it doesn't pop in mid-animation and flash the hub underneath.
  const prevPathnameRef = useRef(pathname);
  const [holdTabBar, setHoldTabBar] = useState(false);
  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    if (hideTabBarForPath(prev) && !hideTabBarForPath(pathname)) {
      setHoldTabBar(true);
      const id = window.setTimeout(() => setHoldTabBar(false), DURATION.page);
      return () => window.clearTimeout(id);
    }
  }, [pathname]);
  const hideTabBar = hideTabBarForPath(pathname) || holdTabBar;

  return (
    <LeagueSwitchProvider>
      <div className="flex h-full flex-col overflow-hidden bg-white md:flex-row md:bg-canvas">
        <SideNav active={activeTab} leagueId={leagueId} onSelect={onSelectTab} />

        <div className="relative flex min-h-0 flex-1 flex-col">
          <NavStack
            interactive
            className={cn(
              'min-h-0 flex-1 md:bg-transparent',
              isDarkRoute(pathname) ? 'bg-[#08080C]' : 'bg-white'
            )}
          >
            {children}
          </NavStack>
          {!hideTabBar && (
            <TabBar
              active={activeTab}
              onSelect={onSelectTab}
              className="fixed inset-x-0 bottom-0 z-[var(--z-chrome)] md:hidden"
              data-app-chrome="tab-bar"
            />
          )}
        </div>
        {/* One-time nudge to start a private mini league, fired at a natural moment
            (after a signed-in player has joined a league and made their first picks). */}
        <MiniLeaguePromo />
      </div>
    </LeagueSwitchProvider>
  );
}

export default function App() {
  const { user, uid, loading } = useAuth();
  const { data: profile } = useProfile(uid);
  const { pathname } = useLocation();

  // OTA live updates (native only). Mounted before any early return so it always
  // runs on launch regardless of auth/route; reads the world-readable config/ota.
  useOtaUpdater();

  // Public, auth-free icon preview (used to screenshot the mark at 1080×1080).
  if (pathname === '/dev/icon') return <IconShowcase />;

  // Dev-only sport-icon comparison page for choosing the icon family.
  if (pathname === '/dev/icons') return <SportIconShowcase />;

  // Web-only admin dashboard. Never mounted inside the native iOS app, and lives
  // outside the player auth/chrome flow — it has its own password gate.
  if (pathname.startsWith('/admin') && !Capacitor.isNativePlatform()) return <AdminApp />;

  // The branded splash is the LAUNCH screen — shown once while the app boots, then
  // never again (a native app doesn't re-flash its launch screen mid-session). The
  // boot gate is: auth still resolving, or signed in but the profile doc hasn't
  // loaded yet. We key the profile step on `profile === undefined` (the true
  // "first load not finished" signal) rather than React Query's isLoading, which
  // reads false during the persisted-cache restore window — that gap would
  // otherwise flash CreateProfile at a returning, fully-onboarded player. Once
  // resolved, profile is always User|null, so this can't re-trigger after boot.
  if (loading) return <SplashScreen />;
  if (!user) return <AuthFlow />;
  if (profile === undefined) return <SplashScreen />;
  if (!profile || profile.profileComplete === false) return <CreateProfile />;

  return (
    <AppChrome>
      {(location) => (
        <Routes location={location}>
          <Route path="/" element={<ChooseSport />} />
          <Route path="/football" element={<ChooseLeague />} />
          <Route path="/play/:leagueId" element={<RoundPlay />} />
          <Route path="/play/:leagueId/picks" element={<PicksRoute />} />
          <Route path="/play/:leagueId/waiting" element={<RoundWaiting />} />
          <Route path="/play/:leagueId/results" element={<RoundResults />} />
          <Route path="/play/:leagueId/fixture/:fixtureId" element={<MatchDetail />} />
          <Route path="/play/:leagueId/standings" element={<Standings />} />
          <Route path="/play/:leagueId/friends" element={<Friends />} />
          <Route path="/play/:leagueId/profile" element={<Profile />} />
          <Route path="/play/:leagueId/notifications" element={<Notifications />} />
          {/* Back-compat: bare tab routes resolve the active league and forward. */}
          <Route path="/standings" element={<RedirectToLeague tab="standings" />} />
          <Route path="/friends" element={<RedirectToLeague tab="friends" />} />
          <Route path="/profile" element={<RedirectToLeague tab="profile" />} />
          <Route path="/leagues" element={<LeaguesHub />} />
          <Route path="/leagues/new" element={<CreateMiniLeague />} />
          <Route path="/leagues/join" element={<JoinMiniLeague />} />
          <Route path="/leagues/:id" element={<MiniLeagueStandings />} />
          <Route path="/leagues/:id/manage" element={<ManageMiniLeague />} />
          <Route path="/leagues/:id/share" element={<ShareMiniLeague />} />
          <Route path="/duel/:duelId" element={<HeadToHead />} />
          <Route path="/challenge/:opponentId" element={<ChallengeSetup />} />
          <Route path="/u/:userId" element={<UserProfile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/settings/profile" element={<EditProfile />} />
          <Route path="/pro" element={<GoPro />} />
          <Route path="/notifications" element={<RedirectToLeague tab="notifications" />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </AppChrome>
  );
}

/**
 * Forwards a bare tab route (/standings, /friends, /profile) into the player's
 * active league world, so old links, deep links and the account back-arrow keep
 * working now that these tabs are league-scoped. Players who've joined nothing go
 * to the lobby to pick a league first.
 */
function RedirectToLeague({ tab }: { tab: 'standings' | 'friends' | 'profile' | 'notifications' }) {
  const { leagueId, loading } = useActiveLeague();
  // This resolver runs mid-session inside AppChrome (bare routes + deep links), so
  // it must NOT re-show the launch splash. It resolves from warm cache almost
  // instantly; on a cold cache we show the app's standard quiet loader instead.
  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-[14px] font-medium text-ink2">Loading…</div>
      </div>
    );
  }
  if (!leagueId) return <Navigate to="/football" replace />;
  return <Navigate to={`/play/${leagueId}/${tab}`} replace />;
}
